import React, { useEffect, useMemo, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PublishedWithChangesOutlinedIcon from "@mui/icons-material/PublishedWithChangesOutlined";
import { MEDICAL_DEPLOYMENT_LABELS } from "constants/dashboardTerminology";
import {
  canonicalOperationalStateOptions,
  getEstadoOperativoVisual,
  isTerminalOperationalStatus,
} from "utils/operationalStatus";
import {
  buildSocioPointSignature,
  buildSociosLegend,
  formatCoverageDistance,
  getContrastStrokeColor,
  getContrastTextColor,
  getCoverageVisualOptions,
  getSocioDisplayLabel,
  getSocioStatusLabel,
  isHealthCoverageEnabled,
  normalizeCoverageRadius,
  normalizeMapColor,
  normalizeSocioStatus,
  shouldDisplayCoverage,
  ZOOM_MINIMO_COBERTURA,
} from "./mapaSociosUtils";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const defaultCenter = { lat: 10.66663, lng: -71.61245 };

const isValidVenezuelaCoordinate = (lat, lng) => {
  if (lat === null || lng === null) return false;
  if (Number(lat) === 0 && Number(lng) === 0) return false;

  return lat >= 0 && lat <= 13 && lng >= -74 && lng <= -59;
};

const formatDate = (date) => {
  if (!date || typeof date !== "string" || !date.includes("-")) return date || "Sin fecha";
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
};

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const buildOperationalHoverContent = (point) => {
  const nombre = point.descripcionConvo || point.nombreJornada || "Despliegue sin nombre";
  const proyecto = point.nombreProyecto || point.codigoProyecto || "Sin proyecto";
  const fecha = formatDate(point.fechaJornada || point.fecha);
  const comunidad = point.comunidad || "Sin comunidad";
  const aceptados = Number(point.aceptados || 0);
  const convocados = Number(point.convocados || 0);
  const atenciones = Number(point.atencionesSalud || 0);
  const estado = getEstadoOperativoVisual(point.estadoOperativo).label;

  return `
    <div style="min-width:190px;max-width:250px;font-family:Arial,sans-serif;padding:2px 1px;">
      <div style="font-size:14px;font-weight:700;margin-bottom:4px;color:#0f172a;">
        ${escapeHtml(nombre)}
      </div>
      <div style="font-size:12px;color:#475569;margin-bottom:4px;">
        ${escapeHtml(proyecto)}
      </div>
      <div style="font-size:12px;color:#475569;margin-bottom:6px;">
        ${escapeHtml(fecha)} · ${escapeHtml(comunidad)}
      </div>
      <div style="font-size:12px;color:#475569;margin-bottom:6px;">
        Estado: <strong>${escapeHtml(estado)}</strong>
      </div>
      <div style="display:flex;gap:8px;font-size:12px;font-weight:600;color:#0f172a;">
        <span>${aceptados} aceptados</span>
        <span>${convocados} convocados</span>
      </div>
      ${
        atenciones > 0
          ? `<div style="margin-top:4px;font-size:12px;color:#0369a1;font-weight:600;">
              ${atenciones} atenciones de salud
            </div>`
          : ""
      }
      <div style="margin-top:6px;font-size:11px;color:#64748b;">
        Haga clic para ver más detalles
      </div>
    </div>
  `;
};

const buildHealthHoverContent = (point) => {
  const nombre = point.nombreJornada || point.descripcionConvo || "Despliegue sin nombre";
  const proyecto =
    point.nombreProyecto || point.codigoProyectoVisual || point.codigoProyecto || "Sin proyecto";
  const fecha = formatDate(point.fechaJornada || point.fecha);
  const comunidad = point.comunidad || "Sin comunidad";
  const centro = point.centro || point.puntoEncuentro || "Sin centro";
  const totalAtenciones = Number(point.totalAtenciones || 0);
  const personasAtendidas = Number(point.personasAtendidas || 0);
  const estadoSocio = normalizeSocioStatus(point.estadoSocioMapa);
  const nombreSocio = String(point.nombreSocio || "").trim() || getSocioStatusLabel(estadoSocio);
  const abreviaturaSocio = String(point.abreviaturaSocio || "").trim();
  const cobertura = formatCoverageDistance(point.radioCoberturaMetros);

  return `
    <div style="min-width:190px;max-width:250px;font-family:Arial,sans-serif;padding:2px 1px;">
      <div style="font-size:14px;font-weight:700;margin-bottom:4px;color:#0f172a;">
        ${escapeHtml(nombre)}
      </div>
      <div style="font-size:12px;color:#475569;margin-bottom:4px;">
        ${escapeHtml(proyecto)}
      </div>
      <div style="font-size:12px;color:#475569;margin-bottom:4px;">
        ${escapeHtml(fecha)} · ${escapeHtml(comunidad)}
      </div>
      <div style="font-size:12px;color:#475569;margin-bottom:6px;">
        ${escapeHtml(centro)}
      </div>
      <div style="font-size:12px;color:#0f172a;margin-bottom:3px;">
        Socio: <strong>${escapeHtml(nombreSocio)}</strong>
      </div>
      ${
        abreviaturaSocio
          ? `<div style="font-size:12px;color:#475569;margin-bottom:3px;">
              Abreviatura: ${escapeHtml(abreviaturaSocio)}
            </div>`
          : ""
      }
      <div style="font-size:12px;color:#475569;margin-bottom:6px;">
        Cobertura visual: ${escapeHtml(cobertura)}
      </div>
      ${
        estadoSocio && estadoSocio !== "RESUELTO"
          ? `<div style="font-size:11px;color:#b45309;margin-bottom:6px;font-weight:600;">
              Asociación: ${escapeHtml(estadoSocio)}
            </div>`
          : ""
      }
      <div style="display:flex;gap:8px;font-size:12px;font-weight:600;color:#0f172a;">
        <span>${totalAtenciones} atenciones</span>
        <span>${personasAtendidas} personas</span>
      </div>
      <div style="margin-top:6px;font-size:11px;color:#64748b;">
        Haga clic para ver más detalles
      </div>
    </div>
  `;
};

const toValidPoint = (point) => {
  const lat = Number(point?.latitud);
  const lng = Number(point?.longitud);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (!isValidVenezuelaCoordinate(lat, lng)) return null;

  return {
    ...point,
    lat,
    lng,
  };
};

const getMarkerId = (point, index) => {
  return String(
    point.idConvocatoria ||
      point.id_convocatoria ||
      point.idJornada ||
      point.id_jornada ||
      point.codigoJornada ||
      `${point.lat}-${point.lng}-${index}`
  );
};

const createNativeMarkerIcon = ({ maps, mode, estadoOperativo, colorMapa, selected = false }) => {
  const isOperational = mode === "operativo";

  if (isOperational) {
    return {
      path: maps.SymbolPath.CIRCLE,
      fillColor: getEstadoOperativoVisual(estadoOperativo).markerColor,
      fillOpacity: 1,
      strokeColor: selected ? "#0f172a" : "#ffffff",
      strokeWeight: selected ? 5 : 3,
      scale: selected ? 18 : 15,
    };
  }

  const fillColor = normalizeMapColor(colorMapa);

  return {
    path: maps.SymbolPath.CIRCLE,
    fillColor,
    fillOpacity: 1,
    strokeColor: getContrastStrokeColor(fillColor),
    strokeWeight: selected ? 4 : 2,
    scale: selected ? 18 : 15,
  };
};

const getMarkerLabelColor = (markerColor) =>
  ["#eab308", "#f59e0b"].includes(String(markerColor).toLowerCase()) ? "#111827" : "#ffffff";

const OperationalMapLegend = () => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{ flexWrap: "wrap", columnGap: 2, rowGap: 0.75, mb: 1.5 }}
    aria-label="Leyenda de estados operativos"
  >
    {canonicalOperationalStateOptions.map((state) => {
      const visual = getEstadoOperativoVisual(state.value);

      return (
        <Stack key={state.value} direction="row" spacing={0.75} alignItems="center">
          <Box
            component="span"
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: visual.markerColor,
              border: "1px solid rgba(15, 23, 42, 0.18)",
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {visual.label}
          </Typography>
        </Stack>
      );
    })}
  </Stack>
);

const HealthMapLegend = ({ entries }) => (
  <Paper
    elevation={3}
    aria-label="Leyenda de socios visibles"
    sx={{
      position: "absolute",
      left: 12,
      bottom: 34,
      zIndex: 5,
      width: { xs: "calc(100% - 24px)", sm: 300 },
      maxWidth: 320,
      maxHeight: { xs: 142, sm: 180 },
      overflowY: "auto",
      p: 1.25,
      borderRadius: "8px",
      bgcolor: "rgba(255, 255, 255, 0.94)",
      border: "1px solid rgba(15, 23, 42, 0.12)",
    }}
  >
    <Typography variant="caption" sx={{ display: "block", mb: 0.75, fontWeight: 900, color: "#0f172a" }}>
      Socios visibles
    </Typography>
    <Stack spacing={0.65}>
      {entries.map((entry) => (
        <Stack key={entry.key} direction="row" spacing={0.8} alignItems="center">
          <Box
            component="span"
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: entry.colorMapa,
              border: `2px solid ${getContrastStrokeColor(entry.colorMapa)}`,
              color: getContrastTextColor(entry.colorMapa),
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ color: "#334155", fontWeight: 700, lineHeight: 1.25 }}>
            {entry.label} · {entry.cantidadPuntos} {entry.cantidadPuntos === 1 ? "punto" : "puntos"}
          </Typography>
        </Stack>
      ))}
    </Stack>
  </Paper>
);

const getMarkerValue = (point, mode) => {
  if (mode === "operativo") {
    return Number(point.markerValue ?? point.aceptados ?? point.total ?? 0);
  }

  return Number(point.totalAtenciones || 0);
};

const SelectedPointCard = ({
  point,
  onClose,
  mode = "salud",
  onViewVolunteers,
  onManageOperationalStatus,
  onViewOperationalHistory,
}) => {
  const theme = useTheme();

  if (!point) return null;

  if (mode === "operativo") {
    const estadoVisual = getEstadoOperativoVisual(point.estadoOperativo);
    const terminalState = isTerminalOperationalStatus(point.estadoOperativo);
    const hasConvocatoria = Boolean(point.idConvocatoria || point.id_convocatoria || point.id);

    return (
      <Paper
        elevation={4}
        sx={{
          mt: 2,
          p: 2,
          borderRadius: "16px",
          border: `1px solid ${estadoVisual.border}`,
          borderLeft: `5px solid ${estadoVisual.border}`,
          bgcolor:
            theme.palette.mode === "dark" ? theme.palette.background.alt : estadoVisual.background,
        }}
      >
        <Stack spacing={1}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                {point.descripcionConvo ||
                  point.nombreJornada ||
                  `${MEDICAL_DEPLOYMENT_LABELS.singularShort} sin nombre`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(point.fechaJornada || point.fecha)}
              </Typography>
            </Box>

            <Chip label="Cerrar" size="small" variant="outlined" onClick={onClose} sx={{ fontWeight: 800 }} />
          </Box>

          <Typography variant="body2" color="text.secondary">
            {point.nombreProyecto || point.codigoProyectoVisual || point.codigoProyecto || "Sin proyecto"}
          </Typography>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {point.comunidad || "Sin comunidad"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[point.estado, point.municipio, point.parroquia].filter(Boolean).join(" / ") || "Sin territorio"}
            </Typography>
            <Chip
              label={estadoVisual.label}
              color={estadoVisual.chipColor}
              size="small"
              variant="filled"
              sx={{ mt: 0.75, fontWeight: 900 }}
            />
          </Box>

          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
            <Chip label={`${Number(point.convocados || 0)} convocados`} size="small" variant="outlined" />
            <Chip label={`${Number(point.aceptados || 0)} aceptados`} size="small" color="primary" />
            <Chip label={`${Number(point.enEspera || 0)} en espera`} size="small" variant="outlined" />
            <Chip
              label={`${Number(point.asistenciasRegistradas || 0)} asistencias`}
              size="small"
              variant="outlined"
            />
            <Chip label={`${Number(point.atencionesSalud || 0)} atenciones salud`} size="small" variant="outlined" />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} sx={{ flexWrap: "wrap", gap: 1 }}>
            {onManageOperationalStatus && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<PublishedWithChangesOutlinedIcon />}
                onClick={() => {
                  onClose?.();
                  onManageOperationalStatus(point);
                }}
                disabled={!hasConvocatoria || terminalState}
              >
                Gestionar estado
              </Button>
            )}

            {onViewOperationalHistory && (
              <Button
                variant="outlined"
                color="info"
                size="small"
                startIcon={<HistoryOutlinedIcon />}
                onClick={() => {
                  onClose?.();
                  onViewOperationalHistory(point);
                }}
                disabled={!hasConvocatoria}
              >
                Historial
              </Button>
            )}

            {onViewVolunteers && (
              <Button
                variant="contained"
                size="small"
                startIcon={<GroupsOutlinedIcon />}
                onClick={() => {
                  onClose?.();
                  onViewVolunteers(point);
                }}
                disabled={!hasConvocatoria}
                sx={{
                  bgcolor: "#6d28d9",
                  "&:hover": { bgcolor: "#5b21b6" },
                }}
              >
                Ver voluntarios
              </Button>
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            El marcador representa {point.markerMetricLabel || "Aceptados"}.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  const socioColor = normalizeMapColor(point.colorMapa);
  const estadoSocio = normalizeSocioStatus(point.estadoSocioMapa);
  const socioLabel = getSocioDisplayLabel(point);
  const coberturaLabel = formatCoverageDistance(point.radioCoberturaMetros);

  return (
    <Paper
      elevation={4}
      sx={{
        mt: 2,
        p: 2,
        borderRadius: "16px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        bgcolor: theme.palette.background.alt,
      }}
    >
      <Stack spacing={1}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
              {point.nombreJornada || `${MEDICAL_DEPLOYMENT_LABELS.singularShort} sin nombre`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(point.fechaJornada)}
            </Typography>
          </Box>

          <Chip label="Cerrar" size="small" variant="outlined" onClick={onClose} sx={{ fontWeight: 800 }} />
        </Box>

        <Typography variant="body2" color="text.secondary">
          {[point.estado, point.municipio, point.parroquia].filter(Boolean).join(" / ") || "Sin territorio"}
        </Typography>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {point.comunidad || "Sin comunidad"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {point.centro || "Sin centro"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {point.puntoEncuentro || "Sin punto de encuentro"}
          </Typography>
        </Box>

        <Stack spacing={0.6}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <Box
              component="span"
              aria-label={`Color de ${socioLabel}`}
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                bgcolor: socioColor,
                border: `2px solid ${getContrastStrokeColor(socioColor)}`,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              Socio: {socioLabel}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Cobertura estimada: {coberturaLabel}
          </Typography>
          {estadoSocio !== "RESUELTO" && (
            <Typography variant="caption" sx={{ color: "#b45309", fontWeight: 800 }}>
              {getSocioStatusLabel(estadoSocio)}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
          <Chip label={`${Number(point.totalAtenciones || 0)} atenciones`} size="small" color="primary" />
          <Chip label={`${Number(point.personasAtendidas || 0)} personas`} size="small" variant="outlined" />
          <Chip label={`${Number(point.comunidadesAtendidas || 0)} comunidades`} size="small" variant="outlined" />
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {point.nombreProyecto || point.codigoProyectoVisual || point.codigoProyecto || "Sin proyecto"}
        </Typography>
      </Stack>
    </Paper>
  );
};

export default function JornadasMap({
  points = [],
  height = 420,
  mode = "salud",
  onViewVolunteers,
  onManageOperationalStatus,
  onViewOperationalHistory,
}) {
  const theme = useTheme();
  const markersRef = useRef([]);
  const circlesRef = useRef([]);
  const renderPointsRef = useRef([]);
  const hoverInfoWindowRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [mapApi, setMapApi] = useState(null);
  const [showCoverage, setShowCoverage] = useState(true);
  const [currentZoom, setCurrentZoom] = useState(ZOOM_MINIMO_COBERTURA);

  useEffect(
    () => () => {
      if (hoverCloseTimerRef.current) {
        clearTimeout(hoverCloseTimerRef.current);
        hoverCloseTimerRef.current = null;
      }
      hoverInfoWindowRef.current?.close();
      hoverInfoWindowRef.current = null;
    },
    []
  );

  const validPoints = useMemo(
    () => (Array.isArray(points) ? points.map(toValidPoint).filter(Boolean) : []),
    [points]
  );

  const sociosLegend = useMemo(() => buildSociosLegend(validPoints), [validPoints]);

  const renderPoints = useMemo(() => {
    return validPoints.map((point, index) => ({
      ...point,
      markerId: getMarkerId(point, index),
    }));
  }, [validPoints]);

  renderPointsRef.current = renderPoints;

  const coordinatesSignature = useMemo(
    () =>
      renderPoints
        .map((point) => `${point.markerId}:${point.lat}:${point.lng}`)
        .join("|"),
    [renderPoints]
  );

  const markerDataSignature = useMemo(
    () =>
      renderPoints
        .map((point) =>
          [
            mode === "salud" ? buildSocioPointSignature(point) : "",
            point.markerId,
            point.lat,
            point.lng,
            getMarkerValue(point, mode),
            Number(point.convocados || 0),
            Number(point.aceptados || 0),
            Number(point.enEspera || 0),
            Number(point.asistenciasRegistradas || 0),
            Number(point.atencionesSalud || 0),
            point.estadoOperativo || "",
          ].join(":")
        )
        .join("|"),
    [mode, renderPoints]
  );

  useEffect(() => {
    setSelectedPoint((current) => {
      if (!current) return null;

      return renderPointsRef.current.find((point) => point.markerId === current.markerId) || null;
    });
  }, [markerDataSignature]);

  const center = useMemo(() => {
    if (!validPoints.length) return defaultCenter;

    const totals = validPoints.reduce(
      (acc, point) => ({
        lat: acc.lat + point.lat,
        lng: acc.lng + point.lng,
      }),
      { lat: 0, lng: 0 }
    );

    return {
      lat: totals.lat / validPoints.length,
      lng: totals.lng / validPoints.length,
    };
  }, [validPoints]);

  const zoom = validPoints.length === 1 ? 11 : validPoints.length > 1 ? 11 : 6;

  useEffect(() => {
    if (!isHealthCoverageEnabled(mode) || !mapApi?.map || !mapApi?.maps) return undefined;

    const { map, maps } = mapApi;
    const updateZoom = () => {
      const nextZoom = Number(map.getZoom());
      if (Number.isFinite(nextZoom)) setCurrentZoom(nextZoom);
    };

    updateZoom();
    const zoomListener = map.addListener("zoom_changed", updateZoom);

    return () => {
      maps.event.removeListener(zoomListener);
    };
  }, [mapApi, mode]);

  useEffect(() => {
    const currentPoints = renderPointsRef.current;

    if (!mapApi?.map || !mapApi?.maps || !currentPoints.length) return;

    const { map, maps } = mapApi;

    if (currentPoints.length === 1) {
      map.setCenter({ lat: currentPoints[0].lat, lng: currentPoints[0].lng });
      map.setZoom(13);
      return;
    }

    const bounds = new maps.LatLngBounds();

    currentPoints.forEach((point) => {
      bounds.extend(new maps.LatLng(point.lat, point.lng));
    });

    map.fitBounds(bounds);

    maps.event.addListenerOnce(map, "idle", () => {
      if (map.getZoom() > 14) {
        map.setZoom(14);
      }
      if (map.getZoom() < 11) {
        map.setZoom(11);
      }
    });
  }, [coordinatesSignature, mapApi]);

  useEffect(() => {
    if (!mapApi?.map || !mapApi?.maps) return undefined;

    const { map, maps } = mapApi;
    const currentPoints = renderPointsRef.current;

    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
    hoverInfoWindowRef.current?.close();

    if (!hoverInfoWindowRef.current) {
      hoverInfoWindowRef.current = new maps.InfoWindow({ disableAutoPan: true });
    }

    markersRef.current.forEach((marker) => {
      maps.event.clearInstanceListeners(marker);
      marker.setMap(null);
    });
    markersRef.current = [];

    circlesRef.current.forEach(({ circle }) => {
      circle.setMap(null);
    });
    circlesRef.current = [];

    if (!currentPoints.length) return undefined;

    currentPoints.forEach((point) => {
      const isSelected = selectedPoint?.markerId === point.markerId;
      const operationalMarkerColor =
        mode === "operativo" ? getEstadoOperativoVisual(point.estadoOperativo).markerColor : null;

      const marker = new maps.Marker({
        position: {
          lat: point.lat,
          lng: point.lng,
        },
        map,
        title:
          point.comunidad ||
          point.centro ||
          point.nombreJornada ||
          MEDICAL_DEPLOYMENT_LABELS.singularShort,
        icon: createNativeMarkerIcon({
          maps,
          mode,
          estadoOperativo: point.estadoOperativo,
          colorMapa: point.colorMapa,
          selected: isSelected,
        }),
        label: {
          text: String(getMarkerValue(point, mode)),
          color:
            mode === "operativo"
              ? getMarkerLabelColor(operationalMarkerColor)
              : getContrastTextColor(normalizeMapColor(point.colorMapa)),
          fontSize: isSelected ? "13px" : "12px",
          fontWeight: "900",
        },
        zIndex: isSelected ? 999 : 10,
      });

      if (isHealthCoverageEnabled(mode)) {
        const colorSocio = normalizeMapColor(point.colorMapa);
        const circle = new maps.Circle({
          center: {
            lat: point.lat,
            lng: point.lng,
          },
          radius: normalizeCoverageRadius(point.radioCoberturaMetros),
          clickable: false,
          map: null,
          ...getCoverageVisualOptions(colorSocio, isSelected),
        });

        circlesRef.current.push({
          circle,
          markerId: point.markerId,
          colorSocio,
        });
      }

      marker.addListener("mouseover", () => {
        if (hoverCloseTimerRef.current) {
          clearTimeout(hoverCloseTimerRef.current);
          hoverCloseTimerRef.current = null;
        }

        const infoWindow = hoverInfoWindowRef.current;
        if (!infoWindow) return;

        const content =
          mode === "operativo"
            ? buildOperationalHoverContent(point)
            : buildHealthHoverContent(point);

        infoWindow.setContent(content);
        infoWindow.open({ map, anchor: marker, shouldFocus: false });
      });

      marker.addListener("mouseout", () => {
        if (hoverCloseTimerRef.current) {
          clearTimeout(hoverCloseTimerRef.current);
        }

        hoverCloseTimerRef.current = setTimeout(() => {
          hoverInfoWindowRef.current?.close();
          hoverCloseTimerRef.current = null;
        }, 120);
      });

      marker.addListener("click", () => {
        if (hoverCloseTimerRef.current) {
          clearTimeout(hoverCloseTimerRef.current);
          hoverCloseTimerRef.current = null;
        }
        hoverInfoWindowRef.current?.close();
        setSelectedPoint(point);
      });

      markersRef.current.push(marker);
    });

    return () => {
      if (hoverCloseTimerRef.current) {
        clearTimeout(hoverCloseTimerRef.current);
        hoverCloseTimerRef.current = null;
      }
      hoverInfoWindowRef.current?.close();

      markersRef.current.forEach((marker) => {
        maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
      });
      markersRef.current = [];

      circlesRef.current.forEach(({ circle }) => {
        circle.setMap(null);
      });
      circlesRef.current = [];
    };
  }, [mapApi, markerDataSignature, mode, selectedPoint?.markerId]);

  useEffect(() => {
    if (!isHealthCoverageEnabled(mode) || !mapApi?.map) return;

    circlesRef.current.forEach(({ circle, markerId, colorSocio }) => {
      const isSelected = selectedPoint?.markerId === markerId;
      circle.setOptions(getCoverageVisualOptions(colorSocio, isSelected));
      circle.setMap(
        shouldDisplayCoverage({
          mode,
          showCoverage,
          currentZoom,
          isSelected,
        })
          ? mapApi.map
          : null
      );
    });
  }, [currentZoom, mapApi, markerDataSignature, mode, selectedPoint?.markerId, showCoverage]);

  if (!renderPoints.length) {
    return (
      <>
        {mode === "operativo" && <OperationalMapLegend />}
        <Box
          height={height}
          display="flex"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          sx={{
            borderRadius: "16px",
            border: "1px dashed rgba(15, 23, 42, 0.22)",
            bgcolor: theme.palette.mode === "dark" ? theme.palette.primary[600] : "#f8fbfb",
            px: 2,
          }}
        >
          <Stack spacing={1} alignItems="center">
            <PlaceOutlinedIcon sx={{ fontSize: 42, color: "teal" }} />
            <Typography variant="body1" sx={{ fontWeight: 800 }}>
              No hay {MEDICAL_DEPLOYMENT_LABELS.pluralShort.toLowerCase()} con coordenadas válidas para los
              filtros seleccionados.
            </Typography>
          </Stack>
        </Box>
      </>
    );
  }

  return (
    <>
      {mode === "operativo" && <OperationalMapLegend />}
      <Box
        height={height}
        sx={{
          position: "relative",
          borderRadius: "16px",
          overflow: "hidden",
          border: "1px solid rgba(15, 23, 42, 0.08)",
        }}
      >
        <GoogleMapReact
          bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
          defaultCenter={center}
          defaultZoom={zoom}
          yesIWantToUseGoogleMapApiInternals
          debounced={false}
          onGoogleApiLoaded={({ map, maps }) => {
            setMapApi({ map, maps });
          }}
          options={{
            streetViewControl: false,
            fullscreenControl: true,
            mapTypeControl: true,
            clickableIcons: false,
            gestureHandling: "greedy",
          }}
        />
        {isHealthCoverageEnabled(mode) && (
          <>
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                top: 52,
                right: 12,
                zIndex: 5,
                px: 1,
                py: 0.25,
                borderRadius: "8px",
                bgcolor: "rgba(255, 255, 255, 0.94)",
                border: "1px solid rgba(15, 23, 42, 0.12)",
              }}
            >
              <FormControlLabel
                sx={{ m: 0, color: "#0f172a" }}
                control={
                  <Switch
                    size="small"
                    checked={showCoverage}
                    onChange={(event) => setShowCoverage(event.target.checked)}
                    inputProps={{ "aria-label": "Mostrar áreas de cobertura" }}
                  />
                }
                label={<Typography variant="caption">Mostrar áreas de cobertura</Typography>}
              />
            </Paper>
            <HealthMapLegend entries={sociosLegend} />
          </>
        )}
      </Box>
      <SelectedPointCard
        point={selectedPoint}
        mode={mode}
        onClose={() => setSelectedPoint(null)}
        onViewVolunteers={mode === "operativo" ? onViewVolunteers : undefined}
        onManageOperationalStatus={mode === "operativo" ? onManageOperationalStatus : undefined}
        onViewOperationalHistory={mode === "operativo" ? onViewOperationalHistory : undefined}
      />
    </>
  );
}
